import React, { useEffect, useState, useMemo } from "react";
import { get, post } from "../utils/api";
import { SquarePen, Search, Filter, Download, Plus } from "lucide-react";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import CustomDialog from "../components/dialog/CustomDialog";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Check } from "lucide-react";
import Select from "react-select";

const FundMasterPage = () => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editFund, setEditFund] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mappingData, setMappingData] = useState({
    products: [],
    productTypes: [],
  });
  const [lobs, setLobs] = useState([]);
  const [subLobs, setSubLobs] = useState([]);
  const [allSubLobs, setAllSubLobs] = useState([]);

  const [formData, setFormData] = useState({
    id: null,
    product_id: "",
    product_type_id: "",
    fund_name: "",
    fund_code: "",
    lob_id: "",
    sub_lob_id: "",
    is_active: true,
    is_triparty_agreement: false,
  });

  const [errors, setErrors] = useState({
    product_id: false,
    product_type_id: false,
    fund_name: false,
    fund_code: false,
    lob_id: false,
    sub_lob_id: false,
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch LOBs from API
  const fetchLobs = async () => {
    try {
      const res = await get("/lob/fetchActiveLobs");
      if (res.data.Success && res.data.Data) {
        setLobs(res.data.Data);
      } else {
        setLobs([]);
      }
    } catch (err) {
      console.error(err);
      setLobs([]);
    }
  };

  // Fetch all Sub-LOBs from API
  const fetchAllSubLobs = async () => {
    try {
      const res = await get("/sub-lob/fetchActiveSubLobs");
      if (res.data.Success && res.data.Data) {
        setAllSubLobs(res.data.Data);
      } else {
        setAllSubLobs([]);
      }
    } catch (err) {
      console.error(err);
      setAllSubLobs([]);
    }
  };

  // Fetch Sub-LOBs by LOB ID
  const fetchSubLobsByLob = async (lobId) => {
    try {
      const res = await post("/sub-lob/fetchSubLobsByLob", { lob_id: lobId });
      if (res.data.Success && res.data.Data) {
        setSubLobs(res.data.Data);
      } else {
        setSubLobs([]);
      }
    } catch (err) {
      console.error(err);
      setSubLobs([]);
    }
  };

  // Helper function to get LOB name by ID
  const getLobName = (lobId) => {
    const lob = lobs.find(l => l.id === lobId);
    return lob ? lob.lob_name : `LOB ${lobId}`;
  };

  // Helper function to get Sub-LOB name by ID
  const getSubLobName = (subLobId) => {
    const subLob = allSubLobs.find(sl => sl.id === subLobId);
    return subLob ? subLob.sub_lob_name : `SubLOB ${subLobId}`;
  };

  // Fetch funds data with proper label mapping
  const fetchFunds = async () => {
    setLoading(true);
    try {
      const response = await get("/fund/fetchFunds");
      if (response.data?.Success) {
        const fundsWithLabels = response.data.Data.map((fund) => {
          // Get product and product type labels
          const product = mappingData.products.find(p => p.id === fund.product_id);
          const productType = mappingData.productTypes.find(pt => pt.id === fund.product_type_id);

          return {
            ...fund,
            product: product?.product_code || `Product ${fund.product_id}`,
            product_type: productType?.product_type_code || `Type ${fund.product_type_id}`,
            lob: getLobName(fund.lob_id),
            sub_lob: getSubLobName(fund.sub_lob_id),
            is_active: fund.is_active ?? true,
          };
        });
        setFunds(fundsWithLabels);
      } else {
        setFunds([]);
        toast.info(response.data?.Message || "No funds found");
      }
    } catch (error) {
      console.error("Failed to fetch funds:", error);
      setFunds([]);
      toast.error("Failed to fetch funds");
    } finally {
      setLoading(false);
    }
  };

  // Fetch mapping data for products and product types
  const fetchMappings = async () => {
    try {
      const res = await get("/product/fetchProductTypeMappingDetails");
      const d = res.data?.Data?.[0];
      if (d) {
        setMappingData({
          products: d.product_mst_details || [],
          productTypes: d.product_type_mst_details || [],
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchMappings();
    fetchLobs();
    fetchAllSubLobs();
  }, []);

  // Refresh funds when mapping data is loaded
  useEffect(() => {
    if (mappingData.products.length > 0 && lobs.length > 0 && allSubLobs.length > 0) {
      fetchFunds();
    }
  }, [mappingData, lobs, allSubLobs]);

  // Fetch Sub-LOBs when LOB changes in form
  useEffect(() => {
    if (formData.lob_id) {
      fetchSubLobsByLob(parseInt(formData.lob_id));
    } else {
      setSubLobs([]);
    }
  }, [formData.lob_id]);

  const selectedProductCode = mappingData.products.find(
    (p) => String(p.id) === String(formData.product_id)
  )?.product_code;

  const filteredTypes = useMemo(() => {
    if (selectedProductCode === "AIF") {
      return mappingData.productTypes.filter((pt) =>
        ["CAT II", "CAT III"].includes(pt.product_type_code.toUpperCase())
      );
    } else if (selectedProductCode === "PMS") {
      return mappingData.productTypes.filter((pt) =>
        ["DISCRETIONARY", "NON-DISCRETIONARY", "ADVISORY"].includes(
          pt.product_type_code.toUpperCase()
        )
      );
    }
    return [];
  }, [selectedProductCode, mappingData.productTypes]);

  const filteredFunds = useMemo(() => {
    if (!searchTerm) return funds;
    const lower = searchTerm.toLowerCase();
    return funds.filter(
      (fund) =>
        (fund.fund_name && fund.fund_name.toLowerCase().includes(lower)) ||
        (fund.fund_code && fund.fund_code.toLowerCase().includes(lower)) ||
        (fund.id && String(fund.id).toLowerCase().includes(lower)) ||
        (fund.product && fund.product.toLowerCase().includes(lower)) ||
        (fund.product_type && fund.product_type.toLowerCase().includes(lower))
    );
  }, [funds, searchTerm]);

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        Cell: ({ row }) => (
          <Button
            variant="ghost"
            className="text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded-lg transition-all"
            onClick={() => handleOpenDialog(row.original)}
          >
            <SquarePen size={18} />
          </Button>
        ),
        disableSortBy: true,
        disableFilters: true,
        size: 80,
      },
      {
        Header: "ID",
        accessor: "id",
        size: 70,
        Cell: ({ value }) => <span className="font-normal text-gray-900">{value}</span>
      },
      {
        Header: "Product",
        accessor: "product",
        size: 100,
        Cell: ({ value }) => <span className="font-normal text-gray-800 uppercase leading-tight inline-block max-w-[90px] break-words">{value}</span>
      },
      {
        Header: "Product Type",
        accessor: "product_type",
        size: 150,
        Cell: ({ value }) => {
          let styles = "bg-gray-100 text-gray-600";
          const type = String(value).toUpperCase();

          if (type.includes("CAT")) styles = "bg-indigo-50 text-indigo-600 border border-indigo-100";
          else if (["DISCRETIONARY", "NON-DISCRETIONARY", "ADVISORY"].includes(type)) styles = "bg-cyan-50 text-cyan-600 border border-cyan-100";

          return (
            <span className={`px-3 py-1 rounded-md text-[10px] font-normal uppercase tracking-wider ${styles}`}>
              {value}
            </span>
          );
        },
      },
      {
        Header: "Fund Name",
        accessor: "fund_name",
        size: 200,
        Cell: ({ value }) => (
          <div className="font-normal text-gray-800 uppercase leading-tight max-w-[180px] whitespace-normal line-clamp-2 break-words mx-auto" title={value}>
            {value}
          </div>
        )
      },
      {
        Header: "Fund Code",
        accessor: "fund_code",
        size: 150,
        Cell: ({ value }) => <span className="font-normal text-gray-800">{value}</span>
      },
      {
        Header: "LOB",
        accessor: "lob",
        size: 150,
        Cell: ({ value }) => <span className="font-normal text-gray-800 text-[13px] uppercase tracking-tight">{value}</span>
      },
      {
        Header: "Sub LOB",
        accessor: "sub_lob",
        size: 150,
        Cell: ({ value }) => <span className="font-normal text-gray-800 text-[13px] uppercase tracking-tight">{value}</span>
      },
      {
        Header: "Status",
        accessor: "is_active",
        Cell: ({ value }) => {
          const statusText = value ? "Active" : "Inactive";
          const bgColor = value ? "bg-[#EBFFF3]" : "bg-[#F3F4F6]";
          const textColor = value ? "text-[#10B981]" : "text-[#6B7280]";
          const dotColor = value ? "bg-[#10B981]" : "bg-[#9CA3AF]";

          return (
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-bold w-fit mx-auto`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {statusText}
            </div>
          );
        },
        size: 150,
      },
      {
        Header: "Tri-Party",
        accessor: "is_triparty_agreement",
        Cell: ({ value }) => {
          const text = value ? "Yes" : "No";
          const bgColor = value ? "bg-[#EEF2FF]" : "bg-[#F3F4F6]";
          const textColor = value ? "text-[#6366F1]" : "text-[#6B7280]";
          const dotColor = value ? "bg-[#6366F1]" : "bg-[#9CA3AF]";

          return (
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full border ${bgColor} ${textColor} text-[11px] font-bold w-fit mx-auto`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              {text}
            </div>
          );
        },
        size: 150,
      },
    ],
    []
  );

  const handleOpenDialog = async (row = null) => {
    if (row) {
      try {
        const res = await post("/fund/fetchFundById", {
          id: row.id,
        });
        const data = res.data?.Data?.[0];
        if (data) {
          setFormData({
            id: data.id,
            product_id: String(data.product_id),
            product_type_id: String(data.product_type_id),
            fund_name: data.fund_name || "",
            fund_code: data.fund_code || "",
            lob_id: String(data.lob_id),
            sub_lob_id: String(data.sub_lob_id),
            is_active: data.is_active,
            is_triparty_agreement: data.is_triparty_agreement ?? false,
          });
          setEditFund(row);
          setDialogVisible(true);
        } else {
          toast.error("No record found.");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error loading fund for edit.");
      }
    } else {
      setFormData({
        id: null,
        product_id: "",
        product_type_id: "",
        fund_name: "",
        fund_code: "",
        lob_id: "",
        sub_lob_id: "",
        is_active: true,
        is_triparty_agreement: false,
      });
      setEditFund(null);
      setDialogVisible(true);
    }
    setErrors({
      product_id: false,
      product_type_id: false,
      fund_name: false,
      fund_code: false,
      lob_id: false,
      sub_lob_id: false,
    });
    setFieldErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Restrict fund_name to letters and spaces
    if (name === "fund_name") {
      const regex = /^[A-Za-z\s]*$/;
      if (!regex.test(value)) return;
    }

    // Restrict fund_code to alphanumeric
    if (name === "fund_code") {
      const regex = /^[A-Za-z0-9-]*$/;
      if (!regex.test(value)) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for the field on input change
    setErrors((prev) => ({ ...prev, [name]: false }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({ ...prev, is_active: e.target.checked }));
  };

  const handleTriPartyChange = (e) => {
    setFormData((prev) => ({ ...prev, is_triparty_agreement: e.target.checked }));
  };

  const isFundCodeUnique = (code) => {
    return !funds.some(
      (fund) =>
        fund.fund_code.toLowerCase() === code.toLowerCase() &&
        fund.id !== formData.id
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = formData.fund_name.trim();
    const trimmedCode = formData.fund_code.trim();

    let hasError = false;
    const newErrors = {};
    const fieldErrorMessages = {};

    if (!formData.product_id) {
      fieldErrorMessages.product_id = "This field is mandatory.";
      newErrors.product_id = true;
      hasError = true;
    }

    if (!formData.product_type_id) {
      fieldErrorMessages.product_type_id = "This field is mandatory.";
      newErrors.product_type_id = true;
      hasError = true;
    }

    if (!trimmedName) {
      newErrors.fund_name = true;
      hasError = true;
    }

    if (!trimmedCode) {
      newErrors.fund_code = true;
      hasError = true;
    }

    if (!formData.lob_id) {
      fieldErrorMessages.lob_id = "This field is mandatory.";
      newErrors.lob_id = true;
      hasError = true;
    }

    if (!formData.sub_lob_id) {
      fieldErrorMessages.sub_lob_id = "This field is mandatory.";
      newErrors.sub_lob_id = true;
      hasError = true;
    }

    if (trimmedCode && !isFundCodeUnique(trimmedCode)) {
      fieldErrorMessages.fund_code = "Fund Code must be unique.";
      newErrors.fund_code = true;
      hasError = true;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    setFieldErrors(fieldErrorMessages);

    if (hasError) {
      if (fieldErrorMessages.product_id) toast.error("Product is mandatory.");
      if (fieldErrorMessages.product_type_id) toast.error("Product Type is mandatory.");
      if (!trimmedName) toast.error("Fund Name is mandatory.");
      if (!trimmedCode) toast.error("Fund Code is mandatory.");
      if (fieldErrorMessages.lob_id) toast.error("LOB is mandatory.");
      if (fieldErrorMessages.sub_lob_id) toast.error("Sub LOB is mandatory.");
      if (fieldErrorMessages.fund_code) toast.error("Fund Code must be unique.");
      return;
    }

    try {
      const submitData = {
        ...formData,
        product_id: parseInt(formData.product_id),
        product_type_id: parseInt(formData.product_type_id),
        fund_name: trimmedName,
        fund_code: trimmedCode,
        lob_id: parseInt(formData.lob_id),
        sub_lob_id: parseInt(formData.sub_lob_id),
        is_active: formData.is_active,
        user_Id: 1,
      };

      console.log("Submitting data:", submitData);

      if (submitData.id) {
        const res = await post("/fund/updateFund", submitData);
        if (res.data.Success) {
          toast.success("Fund updated successfully");
          setDialogVisible(false);
          fetchFunds();
        } else {
          toast.error(res.data.Message || "Update failed");
        }
      } else {
        const res = await post("/fund/createFund", submitData);
        if (res.data.Success) {
          toast.success("Fund created successfully");
          setDialogVisible(false);
          fetchFunds();
        } else {
          toast.error(res.data.Message || "Create failed");
        }
      }
    } catch (error) {
      console.error("Error saving fund:", error);
      toast.error("Error saving fund");
    }
  };

  const exportToCSV = () => {
    if (!funds || funds.length === 0) {
      toast("No data to download");
      return;
    }

    const sortedData = [...funds].sort((a, b) => a.id - b.id);

    const csvHeaders = [
      "ID",
      "Product",
      "Product Type",
      "Fund Name",
      "Fund Code",
      "LOB",
      "Sub LOB",
      "Status",
    ];
    const csvRows = sortedData.map((fund) => [
      fund.id,
      fund.product,
      fund.product_type,
      fund.fund_name,
      fund.fund_code,
      fund.lob,
      fund.sub_lob,
      fund.is_active ? "Active" : "Inactive",
    ]);

    const csvContent = [
      csvHeaders.join(","),
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "FundMasterData.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full max-w-screen-xl mx-auto p-4 h-full min-h-screen bg-gray-50/30 space-y-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800 tracking-tight">Fund Management</h1>
        <p className="text-[11px] text-gray-400 font-medium tracking-wide">Configure and manage investment funds and product codes</p>
      </div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3 w-full max-w-3xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by fund name, code, product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow bg-white h-[42px]"
            />
          </div>
          <button
            type="button"
            onClick={exportToCSV}
            className="flex items-center gap-2 text-gray-700 border border-gray-200 hover:bg-gray-50 bg-white h-[42px] px-4 rounded-lg font-medium shadow-sm transition-colors"
          >
            <Download className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-nowrap">Export To Excel</span>
          </button>
        </div>

        <button
          className="flex items-center gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-sm border-none h-[42px] px-5 rounded-lg transition-colors"
          onClick={() => handleOpenDialog()}
        >
          <Plus className="w-4 h-4" />
          <span className="font-medium text-sm text-nowrap">Add Fund Details</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 bg-white">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            Loading funds...
          </div>
        ) : (
          <>
            <ReusableTable columns={columns} data={filteredFunds} />
            {filteredFunds.length === 0 && (
              <p className="text-center text-gray-500 py-8">No records found matching your search.</p>
            )}
          </>
        )}
      </div>

      <CustomDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        header={
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900">{formData.id ? "Edit Fund" : "Add Fund"}</span>
          </div>
        }
        width="50vw"
      >
        <div className="p-2 space-y-6 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {/* Product Select */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" htmlFor="product_id">
                Product
              </label>
              <Select
                id="product_id"
                value={mappingData.products.find(p => String(p.id) === String(formData.product_id)) ? { label: mappingData.products.find(p => String(p.id) === String(formData.product_id)).product_code, value: formData.product_id } : null}
                onChange={(selected) => handleInputChange({ target: { name: "product_id", value: selected?.value || "" } })}
                options={mappingData.products.map(p => ({ label: p.product_code, value: String(p.id) }))}
                placeholder="Select Product"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: errors.product_id ? '#EF4444' : '#E5E7EB',
                    fontSize: '0.875rem',
                    height: '42px',
                    '&:hover': { borderColor: '#6366F1' },
                    boxShadow: 'none'
                  })
                }}
              />
              {fieldErrors.product_id && (
                <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{fieldErrors.product_id}</p>
              )}
            </div>

            {/* Product Type Select */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" htmlFor="product_type_id">
                Product Type
              </label>
              <Select
                id="product_type_id"
                value={filteredTypes.find(t => String(t.id) === String(formData.product_type_id)) ? { label: filteredTypes.find(t => String(t.id) === String(formData.product_type_id)).product_type_code, value: formData.product_type_id } : null}
                onChange={(selected) => handleInputChange({ target: { name: "product_type_id", value: selected?.value || "" } })}
                options={filteredTypes.map(t => ({ label: t.product_type_code, value: String(t.id) }))}
                isDisabled={!selectedProductCode}
                placeholder="Select Product Type"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: errors.product_type_id ? '#EF4444' : '#E5E7EB',
                    fontSize: '0.875rem',
                    height: '42px',
                    backgroundColor: !selectedProductCode ? '#F9FAFB' : 'white',
                    '&:hover': { borderColor: '#6366F1' },
                    boxShadow: 'none'
                  })
                }}
              />
              {fieldErrors.product_type_id && (
                <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{fieldErrors.product_type_id}</p>
              )}
            </div>

            {/* Fund Name Input */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" htmlFor="fund_name">
                Fund Name
              </label>
              <input
                type="text"
                id="fund_name"
                name="fund_name"
                value={formData.fund_name}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white h-[42px] transition-shadow ${errors.fund_name ? "border-red-500 ring-2 ring-red-50" : ""}`}
                placeholder="Enter Fund Name"
              />
            </div>

            {/* Fund Code Input */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" htmlFor="fund_code">
                Fund Code
              </label>
              <input
                type="text"
                id="fund_code"
                name="fund_code"
                value={formData.fund_code}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white h-[42px] transition-shadow ${errors.fund_code ? "border-red-500 ring-2 ring-red-50" : ""}`}
                placeholder="Enter Fund Code"
              />
              {fieldErrors.fund_code && (
                <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{fieldErrors.fund_code}</p>
              )}
            </div>

            {/* LOB Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" htmlFor="lob_id">
                LOB
              </label>
              <Select
                id="lob_id"
                value={lobs.find(l => String(l.id) === String(formData.lob_id)) ? { label: lobs.find(l => String(l.id) === String(formData.lob_id)).lob_name, value: formData.lob_id } : null}
                onChange={(selected) => {
                  handleInputChange({ target: { name: "lob_id", value: selected?.value || "" } });
                  setFormData((prev) => ({ ...prev, sub_lob_id: "" }));
                }}
                options={lobs.map(l => ({ label: l.lob_name, value: String(l.id) }))}
                placeholder="Select LOB"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: errors.lob_id ? '#EF4444' : '#E5E7EB',
                    fontSize: '0.875rem',
                    height: '42px',
                    '&:hover': { borderColor: '#6366F1' },
                    boxShadow: 'none'
                  })
                }}
              />
              {fieldErrors.lob_id && (
                <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{fieldErrors.lob_id}</p>
              )}
            </div>

            {/* Sub LOB Dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" htmlFor="sub_lob_id">
                Sub LOB
              </label>
              <Select
                id="sub_lob_id"
                value={subLobs.find(s => String(s.id) === String(formData.sub_lob_id)) ? { label: subLobs.find(s => String(s.id) === String(formData.sub_lob_id)).sub_lob_name, value: formData.sub_lob_id } : null}
                onChange={(selected) => handleInputChange({ target: { name: "sub_lob_id", value: selected?.value || "" } })}
                options={subLobs.map(s => ({ label: s.sub_lob_name, value: String(s.id) }))}
                isDisabled={!formData.lob_id}
                placeholder="Select Sub LOB"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: '0.5rem',
                    borderColor: errors.sub_lob_id ? '#EF4444' : '#E5E7EB',
                    fontSize: '0.875rem',
                    height: '42px',
                    backgroundColor: !formData.lob_id ? '#F9FAFB' : 'white',
                    '&:hover': { borderColor: '#6366F1' },
                    boxShadow: 'none'
                  })
                }}
              />
              {fieldErrors.sub_lob_id && (
                <p className="text-red-500 text-[10px] mt-1 font-bold ml-1">{fieldErrors.sub_lob_id}</p>
              )}
            </div>

            {/* Active Switch - show only in edit mode */}
            {formData.id && (
              <div className="flex flex-col col-span-2 mt-2 gap-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Status</label>
                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100 w-fit">
                  <div
                    className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${formData.is_active ? "bg-indigo-500" : "bg-gray-200"
                      }`}
                    onClick={() => handleCheckboxChange({ target: { checked: !formData.is_active } })}
                  >
                    <div
                      className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-transform ${formData.is_active ? "translate-x-6" : "translate-x-1"
                        }`}
                    />
                  </div>
                  <span className={`text-sm font-bold ${formData.is_active ? "text-indigo-600" : "text-gray-500"}`}>
                    {formData.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}

            {/* Tri-Party Agreement Switch */}
            <div className="flex flex-col col-span-2 mt-2 gap-2">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Tri-Party Fund</label>
              <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100 w-fit">
                <div
                  className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${formData.is_triparty_agreement ? "bg-indigo-500" : "bg-gray-200"
                    }`}
                  onClick={() => handleTriPartyChange({ target: { checked: !formData.is_triparty_agreement } })}
                >
                  <div
                    className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-transform ${formData.is_triparty_agreement ? "translate-x-6" : "translate-x-1"
                      }`}
                  />
                </div>
                <span className={`text-sm font-bold ${formData.is_triparty_agreement ? "text-indigo-600" : "text-gray-500"}`}>
                  {formData.is_triparty_agreement ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-8">
            <button
              onClick={() => setDialogVisible(false)}
              className="px-6 py-2 text-sm font-bold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-8 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-200 active:scale-95 transition-all flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {formData.id ? "Update Fund" : "Create Fund"}
            </button>
          </div>
        </div>
      </CustomDialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default FundMasterPage;